/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// analog-switch.js — the vocabulary of the parts that DRIVE NOTHING: the
// CD4066B's bilateral switches and the CD4051B/52B/53B multiplexers (Feature
// 410, phase 2b), the transistors among the discretes, which a logic circuit
// uses as exactly this — a switch a level opens and closes — and the CD4007UB,
// which is six of them in one package.
//
// Every other chip computes a level and puts it on a pin. These connect. Each
// CHANNEL is two terminals and a control rule: while the rule reads HIGH the
// channel JOINS its terminals' nets, so whatever drives one side drives the
// other — either way, which is how one 4051 is a multiplexer and a
// demultiplexer — and while it reads LOW the two sides are apart, each floating
// unless something else drives it. The joining is the engine's (engine.js
// `channelGroups` and `resolveAll`); a def only states its channels, as DATA:
//
//   { a, b, inputs, on }   terminal pins, the control pins the rule reads, and
//                          a pure `on(levels, state)` over those levels → H |
//                          L | X (`state` is the part's own, for the one kind
//                          that has any: a MOSFET's gate charge, below).
//
// The levels arrive read through the part's family reader, so a floating CMOS
// control reads X, and X means "might be on": the engine resolves the circuit
// with and without the channel and keeps what both agree on.
//
// Digital only, as the feature asks: a channel passes LEVELS. Its on-resistance
// and the analog voltages a real one carries are out of scope — which is also
// why a level crossing a channel loses its SUPPLY strength (a rail through a
// switch fights an output on the far side, as it would on a bench). The one
// place the on-resistance shows is the LED rule: at 5 V it limits an LED's
// current as a resistor would (catalog/families.js `ledLimit`).

import { H, L, X, overUnknowns } from "./levels.js";

/** A clean LSB-first list of levels → its number. */
const valueOf = (bits) =>
  bits.reduce((n, lv, i) => n + (lv === H ? 1 << i : 0), 0);

/**
 * Bilateral switches (CD4066B): switch k joins `a` and `b` while its own
 * `control` pin is HIGH.
 * @param {Array<{a:number, b:number, control:number}>} switches
 */
export function bilateralSwitches(switches) {
  return switches.map(({ a, b, control }) => ({
    a,
    b,
    inputs: [control],
    on: ([level]) => (level === H || level === L ? level : X),
  }));
}

/**
 * One analog multiplexer section (a CD4051B is one, a CD4052B two, a CD4053B
 * three): `common` is joined to `channels[k]` while INHIBIT is LOW and the
 * select pins read k (LSB first); INHIBIT HIGH joins nothing.
 * @param {{inh:number, sel:number[], common:number, channels:number[]}} m
 */
export function muxSection({ inh, sel, common, channels }) {
  return channels.map((pin, k) => ({
    a: common,
    b: pin,
    inputs: [inh, ...sel],
    on: (levels) =>
      overUnknowns(levels, ([inhibit, ...select]) =>
        inhibit === L && valueOf(select) === k ? H : L,
      ),
  }));
}

/**
 * MOSFETs on one die (the CD4007UB): each a channel between two pins, closed
 * while its gate reads `onLevel` — HIGH for an N-channel, LOW for a
 * P-channel — exactly as a discrete transistor is (below), less the memory. A
 * gate here is a chip INPUT, read through the CMOS family's reader like every
 * other, so a floating one is "maybe" (X), and the engine keeps what every
 * reading of it agrees on: what the family does with every other floating
 * input.
 * @param {Array<{a:number, b:number, gate:number, onLevel:string}>} fets
 */
export function mosfetChannels(fets) {
  return fets.map(({ a, b, gate, onLevel }) => ({
    a,
    b,
    inputs: [gate],
    on: ([level]) =>
      level === H || level === L ? (level === onLevel ? H : L) : X,
  }));
}

/**
 * A transistor as the switch it is in a logic circuit (the discretes):
 * ONE channel between its two switched pins, closed and opened by its base or
 * gate read as a plain level — no threshold, no gain, no saturation, no
 * on-resistance. `onLevel` is the level that turns it on: HIGH for an NPN or
 * an N-channel MOSFET, LOW for a PNP or a P-channel one.
 *
 * What an UNDEFINED control does — floating, or X — is where the two kinds
 * part company, as they do on a bench:
 *   · a BJT follows its base and remembers nothing: a base nothing drives
 *     passes no base current, so the transistor is off;
 *   · a MOSFET's gate is a capacitor (`holds`): it stays in the state the
 *     last DEFINED level left it in — off until there has been one — so an
 *     undefined gate stops AT the gate instead of spreading down the channel.
 *
 * A holding part is the one channel part with state, `{ gate }` (the last
 * defined level its gate read, null before any), advanced by `step` exactly as
 * a sequential part's is — run-volatile with the rest, so every Run starts
 * with the MOSFET off. Its channel reports `held` while that memory, not the
 * gate, is what decides it, which is how the desk shows it.
 *
 * The control arrives read through the part's own reader, which for a
 * transistor reads a floating pin as undefined (`def.floating`,
 * catalog/families.js) — never as the HIGH a TTL input would read.
 *
 * @param {{control:number, a:number, b:number, onLevel:string, holds:boolean}} t
 * @returns {object} the def's `logic` block
 */
export function transistorSwitch({ control, a, b, onLevel, holds }) {
  const defined = (level) => level === H || level === L;
  const channel = Object.freeze({
    a,
    b,
    inputs: [control],
    on: ([level], state) => {
      const gate = defined(level)
        ? level
        : holds
          ? (state?.gate ?? null)
          : null;
      return gate === onLevel ? H : L;
    },
    held: ([level]) => holds && !defined(level),
  });
  if (!holds) return Object.freeze({ channels: [channel] });
  return Object.freeze({
    channels: [channel],
    state0: () => ({ gate: null }),
    // Returned VERBATIM when nothing defined arrives — the tick's step
    // fixpoint depends on a repeated step changing nothing.
    step: (state, ins) => {
      const level = ins.get(control);
      return defined(level) && level !== state?.gate ? { gate: level } : state;
    },
    outputs: () => new Map(),
  });
}
